import { useQuery } from '@tanstack/react-query';
import { Disc, Download, Loader2, Play } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { fetchRecordings, type MeetRecording } from '../../lib/meetApi';
import {
  MeetRecordingPlayer,
  formatRecordingDuration as formatDuration,
  recordingStartedFormat as startedFormat,
} from './MeetRecordingPlayer';

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * The recordings of one Meet meeting, on its history page: status, length,
 * size and, once the upload to Google Drive is done, play (pop-up) and download.
 * Polls while something is still recording or uploading.
 */
export function MeetRecordingsSection({ meetingId, title }: { meetingId: string; title: string }) {
  const { t } = useI18n();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['meet', 'recordings', meetingId],
    queryFn: () => fetchRecordings(meetingId),
    refetchInterval: (query) => {
      const list = query.state.data?.recordings ?? [];
      return list.some((r) => r.status === 'RECORDING' || r.status === 'UPLOADING') ? 5000 : false;
    },
  });

  const recordings: MeetRecording[] = data?.recordings ?? [];

  return (
    <section>
      <h2 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        <Disc size={14} />
        {t('meet.recordings.title')}
      </h2>
      <div className="rounded-lg border border-border bg-card px-4 py-3">
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            {t('common.loading')}
          </p>
        ) : recordings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {data && !data.enabled ? t('meet.recordings.disabled') : t('meet.recordings.none')}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {recordings.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-sm">
                <span className="text-foreground">
                  {startedFormat.format(new Date(r.startedAt))}
                </span>
                <span className="text-muted-foreground">{formatDuration(r.durationMs)}</span>
                {r.bytes > 0 && (
                  <span className="text-muted-foreground">{formatBytes(r.bytes)}</span>
                )}
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    r.status === 'READY'
                      ? 'bg-emerald-500/15 text-emerald-500'
                      : r.status === 'FAILED'
                        ? 'bg-red-500/15 text-red-500'
                        : 'bg-amber-500/15 text-amber-500'
                  }`}
                >
                  {t(`meet.recordings.status.${r.status}`)}
                </span>
                {r.error && <span className="text-xs text-red-500">{r.error}</span>}
                {r.downloadUrl && (
                  <span className="ml-auto flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setPlayingId(r.id)}
                      className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                    >
                      <Play size={14} />
                      {t('meet.recordings.play')}
                    </button>
                    <a
                      href={r.downloadUrl}
                      download
                      className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                    >
                      <Download size={14} />
                      {t('meet.recordings.download')}
                    </a>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-muted-foreground">{t('meet.recordings.hint')}</p>
      </div>
      {playingId && (
        <MeetRecordingPlayer
          meetingId={meetingId}
          title={title}
          initialRecordingId={playingId}
          onClose={() => setPlayingId(null)}
        />
      )}
    </section>
  );
}
